import { useEffect, useState } from "react"

import { App, Card, Layout, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import NewPasswordForm from "../Components/Forms/NewPasswordForm"
import LoginHeader from "../Components/LoginHeader"
import PrivacyPolicy from "../Components/PrivacyPolicy"
import { useQuery } from "../Utils/utils"
import { authErrorReset } from "./LoginPage/redux/auth.actions"
import { createUserPassword } from "./LoginPage/redux/auth.thunks"
import { confirmEmails } from "./User/services"

const { Text } = Typography

const hasResetPasswordLinkError = (response) => {
  const status = response?.status || response?.response?.status

  return !status || status >= 400
}

function NewPasswordPage({ error, fetching, match, isMobile, ...props }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const { pathname, search } = useLocation()
  let query = useQuery(search)
  let code = query.get("code")
  const [isLinkValidating, setIsLinkValidating] = useState(!!code)

  function handleSubmit(password) {
    if (!code || fetching) return
    props.createUserPassword(password, false, code)
  }

  useEffect(() => {
    document.title = "Create New Password - Chargeback"
  }, [])

  useEffect(() => {
    if (!code) {
      if (pathname.includes("setting")) {
        navigate("/setting/my_profile")
      } else {
        navigate("/login")
      }
    }
  }, [code])

  useEffect(() => {
    let isMounted = true

    const validateResetCode = async () => {
      if (!code) return

      const redirectToLoginWithLinkError = () => {
        notification.error({
          message: t("The Reset Password link has already been used"),
          closeIcon: <Text>{t("Dismiss")}</Text>
        })
        navigate("/login", { replace: true })
      }

      try {
        const response = await confirmEmails({ hash: code, isVerify: false })

        if (!isMounted) return

        if (hasResetPasswordLinkError(response)) {
          redirectToLoginWithLinkError()
        }
      } catch (requestError) {
        if (!isMounted) return

        redirectToLoginWithLinkError()
      } finally {
        if (isMounted) setIsLinkValidating(false)
      }
    }

    validateResetCode()

    return () => {
      isMounted = false
    }
  }, [code, navigate, notification, t])

  useEffect(() => {
    if (error) props.resetError()
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [error])

  if (isLinkValidating) {
    return (
      <Layout style={{ minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </Layout>
    )
  }

  return (
    <Layout>
      <Spin size="large" spinning={fetching || isLinkValidating}>
        <div className="login-row">
          <LoginHeader />
          <div className="login-content">
            <div className="content-main sign-up">
              <Card className="login-card">
                <h2 className="card-title">{t("Create New Password")}</h2>
                <h4 className="card-subtitle-signup">
                  {t("Your new password must be different from previously used passwords")}
                </h4>
                <NewPasswordForm onSubmit={handleSubmit} error={error} />
              </Card>
              <PrivacyPolicy />
            </div>
          </div>
        </div>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state) => ({
  error: state.auth.error,
  fetching: state.auth.fetching,
  isMobile: state.nav.isMobile
})

// wraps dispatch to create nicer functions to call within our component
const mapDispatchToProps = (dispatch) => ({
  resetError: () => dispatch(authErrorReset()),
  createUserPassword: (password, check, confirm_code) =>
    dispatch(createUserPassword({ password, isNewUserCheck: check, confirm_code }))
})

export default connect(mapStateToProps, mapDispatchToProps)(NewPasswordPage)
